import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/api/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SITE_CONTEXT } from './site-context.interceptor';
import { SiteAccessService } from '../services/team/site-access.service';

/**
 * Código con el que el backend deniega una acción en una tienda (autorización por tienda, fase 0).
 * Es un 403 con la sesión todavía válida: se avisa y no se cierra la sesión. Ver
 * doc/epics/team-access/BACKEND_DESIGN_FINAL.md.
 */
export const SITE_ACCESS_DENIED_CODE = '1084';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private router: Router, private auth:AuthService, private injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.auth.isLogged() && error.status === 401) {
          // Clear session tokens from local storage
          localStorage.removeItem("xAuthToken");
          localStorage.removeItem("access_token");

          // Redirect to the login page
          this.router.navigate(['/pages/error/session'], { queryParams: { returnUrl: this.router.url } });
        }
        // La consulta del propio acceso no avisa: si falla, quien la hizo ya redirige o pone candados.
        if (error.status === 403 && error.error?.code === SITE_ACCESS_DENIED_CODE && !request.url.includes('/access/me')) {
          this.notifySiteAccessDenied();
          this.forgetSiteAccess(request);
        }
        if (error.status === 504) {
           // Clear session tokens from local storage
          localStorage.removeItem("xAuthToken");
          localStorage.removeItem("access_token");
          this.router.navigate(['/pages/error/timeout'], { queryParams: { returnUrl: this.router.url } });
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * El rol pudo cambiar o el acceso retirarse: lo cacheado de esa tienda ya no vale. (La consulta del
   * propio acceso no llega aquí: es la que el menú usa para comprobarlo, y haría un bucle.)
   */
  private forgetSiteAccess(request: HttpRequest<unknown>): void {
    const siteId = request.context.get(SITE_CONTEXT);
    if (siteId) this.injector.get(SiteAccessService).markDenied(siteId);
  }

  // TranslateService y ToastrService se piden al usarlos: inyectados en el constructor crearían
  // un ciclo (TranslateService usa HttpClient, que pasa por este interceptor).
  private notifySiteAccessDenied(): void {
    const translate = this.injector.get(TranslateService);
    // Con el aviso estándar del backoffice; preventDuplicates evita uno por cada petición rechazada.
    this.injector.get(ToastrService).error(
      translate.instant('site_access_denied_message'),
      translate.instant('site_access_denied_title'));
  }
}
